import { SearchOutlined } from '@mui/icons-material'
import {
  Autocomplete,
  AutocompleteProps,
  createFilterOptions,
  InputAdornment,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import _, { isEqual } from 'lodash'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { isPromise } from 'util/types'
import { markText } from 'utils/textFilterUtils'

const WorkingStepAutoComplete = memo(
  (props: {
    availableWorkingSteps: Array<WorkingStepType>
    onOptionSelected: (
      workingStepType: WorkingStepType,
      event: React.SyntheticEvent,
    ) => Promise<void> | void
    AutoCompleteProps?: Partial<
      AutocompleteProps<WorkingStepType, false, true, false>
    >
    selectedOption?: WorkingStepType | null
    label?: string
    placeholder?: string
    required?: boolean
    keepFilterAfterSelect?: (workingStepType: WorkingStepType) => boolean
  }) => {
    const { t } = useTranslation()
    const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()
    const { keepFilterAfterSelect, onOptionSelected } = props
    const { filterOptions: customFilterOptions, ...autoCompleteProps } =
      props.AutoCompleteProps ?? {}
    const [inputValue, setInputValue] = useState('')

    useEffect(() => {
      if (props.selectedOption) {
        setInputValue(getLocalizedWorkingStep(props.selectedOption))
      } else {
        setInputValue('')
      }
    }, [props.selectedOption, getLocalizedWorkingStep])

    const handleInputChange = useCallback(
      (_event: React.SyntheticEvent, value: string, reason: string) => {
        if (reason === 'input') {
          setInputValue(value)
        } else if (reason === 'clear') {
          setInputValue('')
        }
        // ignore 'reset' — prevents MUI from filling the input with the selected label
      },
      [],
    )

    const sortedWorkingSteps = React.useMemo(() => {
      return props.availableWorkingSteps.sort((a, b) => {
        return getLocalizedWorkingStep(a).localeCompare(
          getLocalizedWorkingStep(b),
        )
      })
    }, [getLocalizedWorkingStep, props.availableWorkingSteps])

    const defaultFilterOptions = React.useMemo(
      () =>
        createFilterOptions<WorkingStepType>({
          stringify: (option) => getLocalizedWorkingStep(option),
        }),
      [getLocalizedWorkingStep],
    )

    const filterOptions = useCallback<
      NonNullable<
        AutocompleteProps<WorkingStepType, false, true, false>['filterOptions']
      >
    >(
      (options, state) => {
        const availableOptions = customFilterOptions?.(options, state) ?? options

        return defaultFilterOptions(availableOptions, state)
      },
      [customFilterOptions, defaultFilterOptions],
    )

    const savingRef = React.useRef<WorkingStepType | null>(null)
    const handleOptionSelected = useCallback(
      async (
        event: React.SyntheticEvent,
        value: WorkingStepType | null,
        reason: string,
      ) => {
        if (reason === 'selectOption' && value) {
          if (savingRef.current === value) {
            return
          }
          // disable the selected option while the promise is pending
          var li = event.currentTarget as HTMLLIElement
          li.style.pointerEvents = 'none'

          try {
            savingRef.current = value
            if (!keepFilterAfterSelect?.(value)) {
              setInputValue('')
            }

            await onOptionSelected(value, event)
          } finally {
            setTimeout(() => (li.style.pointerEvents = 'auto'))
            savingRef.current = null
          }
        }
      },
      [keepFilterAfterSelect, onOptionSelected],
    )

    return (
      <Autocomplete
        value={props.selectedOption || ({} as WorkingStepType)}
        onDoubleClick={(e) => e.preventDefault()}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleOptionSelected}
        openOnFocus
        autoHighlight
        options={sortedWorkingSteps || []}
        slotProps={{
          listbox: {
            ref: listboxRef,
          },
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              value={
                props.selectedOption
                  ? getLocalizedWorkingStep(props.selectedOption)
                  : ''
              }
              name="workingStep"
              variant="standard"
              // autoFocus
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined color="action" />
                    </InputAdornment>
                  ),
                },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                },
              }}
              placeholder={
                props.placeholder || t('project:working-steps-available')
              }
              label={props.label}
              required={props.required}
            />
          )
        }}
        renderOption={(baseElProps, option, state) => {
          return (
            <ListItem
              disableGutters
              {...baseElProps}
              key={option}
              data-value={option}
            >
              <WorkingStepIconSvg
                attributes={{
                  width: '32px',
                }}
                workingStepType={option}
              />
              <ListItemText
                style={{
                  paddingLeft: '16px',
                }}
                primary={markText(
                  getLocalizedWorkingStep(option),
                  state.inputValue,
                )}
              />
            </ListItem>
          )
        }}
        getOptionLabel={(option) => {
          if (WorkingStepType[option]) {
            return getLocalizedWorkingStep(option)
          } else {
            if (_.isEmpty(option)) {
              return ''
            }
            return option
          }
        }}
        {...autoCompleteProps}
      />
    )
  },
  isEqual,
)

WorkingStepAutoComplete.displayName = 'WorkingStepAutoComplete'

export default WorkingStepAutoComplete
